import { ReactNode } from "react";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface NeumorphicButtonProps {
  children: ReactNode;
  variant?: "raised" | "recessed";
  size?: "sm" | "md" | "lg";
  className?: string;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
}

const NeumorphicButton = ({ 
  children, 
  variant = "raised", 
  size = "md",
  className = "",
  onClick,
  type = "button"
}: NeumorphicButtonProps) => {
  let theme = "light";
  try {
    const themeContext = useNeumorphicTheme();
    theme = themeContext.theme;
  } catch {
    // Not within NeumorphicThemeProvider, use default
  }

  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return "px-4 py-2 text-sm";
      case "md":
        return "px-6 py-3 text-base";
      case "lg":
        return "px-8 py-4 text-lg";
    }
  };

  const getStyles = () => {
    if (theme === "dark") {
      return variant === "raised"
        ? "bg-[#0a0a0a] shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#0d0d0d] text-white"
        : "bg-[#0a0a0a] shadow-[inset_5px_5px_10px_#000000,inset_-5px_-5px_10px_#0d0d0d] text-white";
    } else {
      return variant === "raised"
        ? "bg-[#f5f5f5] shadow-[10px_10px_20px_#d8d8d8,-10px_-10px_20px_#fefefe] text-[#1a1a1a]"
        : "bg-[#f0f0f0] shadow-[inset_5px_5px_10px_#e0e0e0,inset_-5px_-5px_10px_#fefefe] text-[#1a1a1a]";
    }
  };

  const getActiveStyles = () => {
    if (theme === "dark") {
      return "active:shadow-[inset_5px_5px_10px_#000000,inset_-5px_-5px_10px_#0d0d0d]";
    } else {
      return "active:shadow-[inset_5px_5px_10px_#e0e0e0,inset_-5px_-5px_10px_#fefefe]";
    }
  };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      className={cn(
        "rounded-xl font-medium transition-all duration-200 cursor-pointer",
        getSizeStyles(),
        getStyles(),
        getActiveStyles(),
        className
      )}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {children}
    </motion.button>
  );
};

export default NeumorphicButton;
